import React, { useState, useMemo } from 'react';
import {
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { getUser } from '@/lib/common';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PieChartOutlinedIcon from '@mui/icons-material/PieChartOutlined';
import { AppCard } from '@/components/app-card/app-card';
import { ATTESTATION_COLORS } from '@/components/ui';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { useTimeMachineUtcNow } from '@/hooks/useTimeMachineUtcNow';
import { SvgDonut, SvgVBars, ChartLegend } from '@/components/ds/charts';

interface StackedChartDataItem {
  group_name: string;
  attestation_status: string;
  total_forecasted_cost: number;
}

interface PieChartDataItem {
  status: string;
  count: number;
  percentage: number;
  total_forecasted_cost: number;
  cost_percentage: number;
}

interface AttestationStatusChartsProps {
  display: 'all' | 'managed';
  defaultChartScope?: 'resource_owners' | 'systems' | 'departments';
  chartScope?: 'resource_owners' | 'systems' | 'departments';
  data: {
    users: any[];
    systems: any[];
    departments: any[];
    cloudAccounts: any[];
    resources: any[];
    resourceGroups: any[];
    rejectedResources: any[];
  };
}

function AttestationStatusCharts({
  display,
  defaultChartScope = 'resource_owners',
  chartScope: controlledChartScope,
  data,
}: AttestationStatusChartsProps) {
  const [internalChartScope, setInternalChartScope] = useState<
    'resource_owners' | 'systems' | 'departments'
  >(defaultChartScope);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { utcNow } = useTimeMachineUtcNow();
  const monthLabel = useMemo(() => {
    const d = utcNow ?? new Date();
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    return new Date(Date.UTC(y, m, 1)).toLocaleString('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }, [utcNow]);

  const chartScope = controlledChartScope || internalChartScope;

  const formatStatusName = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const handleScopeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newScope: 'resource_owners' | 'systems' | 'departments' | null,
  ) => {
    if (newScope !== null) {
      if (controlledChartScope) {
        return;
      } else {
        setInternalChartScope(newScope);
      }
    }
  };

  const calculateStackedChartData = useMemo((): StackedChartDataItem[] => {
    const currentUser = getUser();
    let filteredResources = data.resources;

    if (display === 'managed' && currentUser) {
      filteredResources = data.resources.filter(
        (resource: any) => resource.owner_id === currentUser.id,
      );
    }

    const groupedData: Record<string, StackedChartDataItem> =
      filteredResources.reduce(
        (acc: Record<string, StackedChartDataItem>, resource: any) => {
          let groupName = '';

          switch (chartScope) {
            case 'resource_owners':
              groupName =
                resource.owner_name || resource.username || 'Unassigned';
              break;
            case 'systems':
              groupName = resource.system_name || 'No system';
              break;
            case 'departments':
              const system = data.systems.find(
                (s: any) => s.id === resource.system_id,
              );
              const department = system
                ? data.departments.find(
                    (d: any) => d.id === system.id_department,
                  )
                : null;
              groupName = department?.name || 'No Department';
              break;
            default:
              groupName = 'Unknown';
          }

          const status = resource.Status || 'PENDING';
          const cost = parseFloat(resource.cost || 0);

          const key = `${groupName}_${status}`;

          if (!acc[key]) {
            acc[key] = {
              group_name: groupName,
              attestation_status: status,
              total_forecasted_cost: 0,
            };
          }

          acc[key].total_forecasted_cost += cost;

          return acc;
        },
        {},
      );

    return Object.values(groupedData);
  }, [data.resources, data.systems, data.departments, display, chartScope]);

  const calculatePieChartData = useMemo((): PieChartDataItem[] => {
    const currentUser = getUser();
    let filteredResources = data.resources;

    if (display === 'managed' && currentUser) {
      filteredResources = data.resources.filter(
        (resource: any) => resource.owner_id === currentUser.id,
      );
    }

    const statusGroups = filteredResources.reduce(
      (
        acc: Record<string, { count: number; total_cost: number }>,
        resource: any,
      ) => {
        const status = resource.Status || 'PENDING';
        const cost = parseFloat(resource.cost || 0);

        if (!acc[status]) {
          acc[status] = { count: 0, total_cost: 0 };
        }

        acc[status].count += 1;
        acc[status].total_cost += cost;

        return acc;
      },
      {},
    );

    const totalCount = Object.values(statusGroups).reduce(
      (sum, group) => sum + group.count,
      0,
    );
    const totalCost = Object.values(statusGroups).reduce(
      (sum, group) => sum + group.total_cost,
      0,
    );

    const allStatuses = ['PENDING', 'CONFIRMED', 'REJECTED'];

    return allStatuses.map((status) => {
      const group = statusGroups[status] || { count: 0, total_cost: 0 };
      return {
        status,
        count: group.count,
        percentage:
          totalCount > 0 ? Math.round((group.count / totalCount) * 100) : 0,
        total_forecasted_cost: group.total_cost,
        cost_percentage:
          totalCost > 0 ? Math.round((group.total_cost / totalCost) * 100) : 0,
      };
    });
  }, [data.resources, display]);

  const getScopeDisplayName = (scope: string) => {
    switch (scope) {
      case 'resource_owners':
        return 'Resource Owners';
      case 'systems':
        return 'Systems';
      case 'departments':
        return 'Departments';
      default:
        return scope;
    }
  };

  const stackedBarChartData = useMemo(() => {
    const chartData = calculateStackedChartData;

    if (!chartData || chartData.length === 0)
      return { categories: [], series: [] };

    const categories = [
      ...new Set(chartData.map((item) => item.group_name)),
    ].filter(Boolean);

    const statuses = [
      ...new Set(chartData.map((item) => item.attestation_status)),
    ].filter(Boolean);

    const series = statuses.map((status, index) => ({
      label: formatStatusName(status),
      data: categories.map((category) => {
        const item = chartData.find(
          (d) => d.group_name === category && d.attestation_status === status,
        );
        return item ? Number(item.total_forecasted_cost || 0) : 0;
      }),
      color:
        ATTESTATION_COLORS[
          status.toLowerCase() as keyof typeof ATTESTATION_COLORS
        ],
    }));

    return { categories, series };
  }, [calculateStackedChartData]);

  const pieChartData = calculatePieChartData.map((item, index) => ({
    id: index,
    value: item.count,
    label: formatStatusName(item.status),
    color:
      ATTESTATION_COLORS[
        item.status.toLowerCase() as keyof typeof ATTESTATION_COLORS
      ],
  }));

  const costPieChartData = calculatePieChartData.map((item, index) => ({
    id: index,
    value: item.total_forecasted_cost,
    label: formatStatusName(item.status),
    color:
      ATTESTATION_COLORS[
        item.status.toLowerCase() as keyof typeof ATTESTATION_COLORS
      ],
  }));

  const totalCount = calculatePieChartData.reduce((s, d) => s + d.count, 0);
  const totalCost = calculatePieChartData.reduce(
    (s, d) => s + d.total_forecasted_cost,
    0,
  );

  // Dominant status (largest slice) shown in the donut centre.
  const dominantCount = calculatePieChartData.reduce(
    (best, d) => (d.count > best.count ? d : best),
    calculatePieChartData[0] ?? { status: '', count: 0, percentage: 0 },
  );
  const dominantCost = calculatePieChartData.reduce(
    (best, d) =>
      d.total_forecasted_cost > best.total_forecasted_cost ? d : best,
    calculatePieChartData[0] ?? {
      status: '',
      total_forecasted_cost: 0,
      cost_percentage: 0,
    },
  );

  const countDonutData = calculatePieChartData.map((d) => ({
    v: totalCount > 0 ? (d.count / totalCount) * 100 : 0,
    c: ATTESTATION_COLORS[
      d.status.toLowerCase() as keyof typeof ATTESTATION_COLORS
    ],
    l: formatStatusName(d.status),
    tv: `${d.count} (${d.percentage}%)`,
  }));

  const costDonutData = calculatePieChartData.map((d) => ({
    v: totalCost > 0 ? (d.total_forecasted_cost / totalCost) * 100 : 0,
    c: ATTESTATION_COLORS[
      d.status.toLowerCase() as keyof typeof ATTESTATION_COLORS
    ],
    l: formatStatusName(d.status),
    tv: `${formatCurrencyNoUnit(d.total_forecasted_cost)} (${d.cost_percentage}%)`,
  }));

  const countLegend = calculatePieChartData.map((d, index) => ({
    id: index,
    color:
      ATTESTATION_COLORS[
        d.status.toLowerCase() as keyof typeof ATTESTATION_COLORS
      ],
    name: formatStatusName(d.status),
    value: `${d.count} (${d.percentage}%)`,
  }));

  const costLegend = calculatePieChartData.map((d, index) => ({
    id: index,
    color:
      ATTESTATION_COLORS[
        d.status.toLowerCase() as keyof typeof ATTESTATION_COLORS
      ],
    name: formatStatusName(d.status),
    value: `${formatCurrencyNoUnit(d.total_forecasted_cost)} (${d.cost_percentage}%)`,
  }));

  // Single-series total forecasted cost per group for the status-by-scope bar.
  const scopeBarData = stackedBarChartData.categories.map((cat, i) => ({
    v: stackedBarChartData.series.reduce(
      (sum, ser) => sum + (Number(ser.data[i]) || 0),
      0,
    ),
    l: String(cat),
  }));

  return (
    <Stack spacing={2}>
      {/* PIE CHARTS */}
      <Grid container sx={{ gap: 2 }}>
        <Grid item xs={12} md sx={{ minWidth: 0 }}>
          <AppCard
            title="Missing Attestation: resource count"
            icon={<PieChartOutlinedIcon color="primary" />}>
            <div
              style={{
                minHeight: '320px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {pieChartData.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  }}>
                  <SvgDonut
                    data={countDonutData}
                    centerTop={formatStatusName(dominantCount.status)}
                    centerBot={`${dominantCount.percentage}%`}
                  />
                  <div style={{ width: '100%', maxWidth: 320, marginTop: 16 }}>
                    <ChartLegend items={countLegend} />
                  </div>
                </div>
              ) : (
                <Typography sx={{ color: '#8A94A6', fontSize: 13 }}>No data available</Typography>
              )}
            </div>
          </AppCard>
        </Grid>

        <Grid item xs={12} md sx={{ minWidth: 0 }}>
          <AppCard
            title={`Missing Attestation: resource cost (${monthLabel} forecast)`}
            icon={<PieChartOutlinedIcon color="primary" />}>
            <div
              style={{
                minHeight: '320px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {costPieChartData.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  }}>
                  <SvgDonut
                    data={costDonutData}
                    centerTop={formatStatusName(dominantCost.status)}
                    centerBot={`${dominantCost.cost_percentage}%`}
                  />
                  <div style={{ width: '100%', maxWidth: 320, marginTop: 16 }}>
                    <ChartLegend items={costLegend} />
                  </div>
                </div>
              ) : (
                <Typography sx={{ color: '#8A94A6', fontSize: 13 }}>No forecasted cost data available</Typography>
              )}
            </div>
          </AppCard>
        </Grid>
      </Grid>

      {/* BAR CHART */}
      <Grid container sx={{ gap: 2 }}>
        <Grid item xs={12} sx={{ flex: 1 }}>
          <AppCard
            title={`Attestation status by ${getScopeDisplayName(chartScope)} (${monthLabel} forecast)`}
            icon={<BarChartOutlinedIcon color="primary" />}
            headerRight={
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {display === 'all' && (
                  <ToggleButtonGroup
                    value={chartScope}
                    exclusive
                    onChange={handleScopeChange}
                    size={isMobile ? 'small' : 'small'}
                    color="primary">
                    <ToggleButton value="resource_owners">
                      {isMobile ? 'Owners' : 'Resource owners'}
                    </ToggleButton>
                    <ToggleButton value="systems">Systems</ToggleButton>
                    <ToggleButton value="departments">
                      {isMobile ? 'Depts' : 'Departments'}
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </div>
            }>
            <div
              style={{
                minHeight: '360px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {scopeBarData.length > 0 ? (
                <div style={{ width: '100%' }}>
                  <SvgVBars
                    data={scopeBarData}
                    h={360}
                    fmt={formatCurrencyNoUnit}
                    color="#1C71E3"
                  />
                </div>
              ) : (
                <Typography sx={{ color: '#8A94A6', fontSize: 13 }}>No data available</Typography>
              )}
            </div>
          </AppCard>
        </Grid>
      </Grid>
    </Stack>
  );
}

export default AttestationStatusCharts;
